import fetchIntercept from "fetch-intercept";
import { get, includes, isEmpty } from "lodash";

import { getAppFeatureProp } from "@/utilities/app-feature-util";
import AuthEnforcer from "@/utilities/auth-enforcer";
import getAppCustomEnv from "@/utilities/get-app-custom-env";
import LocalStoreManager from "@/utilities/local-store-manager";
import { createLogger } from "@/utilities/logger";

import vueStore from "@/store";

import { Constants } from "customer-sdk/constants";

const { SLEEK_APP_CACHE_KEY } = Constants.STORE_CONSTANTS;
const { SLEEK_DOMAINS } = Constants.SLEEK_DOMAINS;
const HTTP_STATUS_CODES = Constants.HTTP_STATUS_CODES;

const filesLogger = createLogger({
  variableFlag: "debugFilesMS",
  name: "Files API",
});
const domain = window.location.hostname;
const isSleekDomain = includes(SLEEK_DOMAINS, domain);

export const getTenantId = () => {
  const appCache = JSON.parse(localStorage.getItem(SLEEK_APP_CACHE_KEY) || "{}");
  const countryCode = get(appCache, "configModule.platformConfig.country_code");
  const partnerId = new LocalStoreManager().getPartnerId();
  const tenantId = countryCode + (partnerId && partnerId !== "null" ? `_${partnerId}` : "");
  return tenantId;
};

const getRequestId = (headers) => {
  return headers.get("X-Request-ID");
};

const getAppOrigin = () => {
  const platformConfig = vueStore.state.configModule.platformConfig;
  const auth0Config = getAppFeatureProp(platformConfig.cmsAppFeatures, "auth0");

  if (process.env.NODE_ENV == "development" && auth0Config) {
    auth0Config["enabled"] = process.env.VUE_APP_IS_AUTH0_ENABLED === "true" ? true : false;
  }

  if (auth0Config) {
    if (localStorage.getItem("isSUT") == "true") {
      return "sut";
    } else {
      return isSleekDomain ? "customer" : "mscomsec";
    }
  } else {
    return undefined;
  }

  // return "test";
};

const getJSONHeader = (token) => {
  let header = {
    Accept: "application/json",
    "Content-Type": "application/json",
    "Tenant-Id": getTenantId(),
    "X-Project-Id": "sleek-customer-common",
    "X-Origin-Pathname": location.pathname,
  };

  if (getAppOrigin()) {
    header["app-origin"] = getAppOrigin();
  }

  if (token) {
    header["Authorization"] = token;
  }

  return header;
};

const getFormDataJSONHeader = (token, pbacAuthorizationToken) => {
  let header = {
    Accept: "application/json",
    "Tenant-Id": getTenantId(),
  };

  if (getAppOrigin()) {
    header["app-origin"] = getAppOrigin();
  }

  if (token) {
    header["Authorization"] = token;
  }

  if (pbacAuthorizationToken) {
    header["pbac_authorization"] = pbacAuthorizationToken;
  }

  return header;
};

const getFileHeader = () => {
  const header = {
    Accept: "*/*",
    "Tenant-Id": getTenantId(),
  };

  if (getAppOrigin()) {
    header["app-origin"] = getAppOrigin();
  }

  return header;
};

let buildJSONForPOSTRequest = (params) => {
  return {
    method: "POST",
    headers: getJSONHeader(),
    body: JSON.stringify(params),
  };
};

let buildJSONForPUTRequest = (params) => {
  return {
    method: "PUT",
    headers: getJSONHeader(),
    body: JSON.stringify(params),
  };
};

let buildJSONForGETRequest = () => {
  return {
    method: "GET",
    headers: getJSONHeader(),
  };
};

let buildJSONForFileGETRequest = () => {
  return {
    method: "GET",
    headers: getFileHeader(),
  };
};

let buildJSONForDELETERequest = (params) => {
  return {
    method: "DELETE",
    headers: getJSONHeader(),
    body: JSON.stringify(params),
  };
};

function parseQueryParams() {
  var vars = {};
  window.location.href.replace(/[?&]+([^=&]+)=([^&]*)/gi, function (m, key, value) {
    vars[key] = value;
  });

  return vars;
}

let getOneTimeToken = () => {
  const params = parseQueryParams();
  return params.oneTimeToken ? params.oneTimeToken.replace("#", "") : null;
};

let buildAuthenticatedJsonForPOSTRequest = (params, authToken, pbacAuthorizationToken) => {
  let output = buildJSONForPOSTRequest(params);

  output.headers.Authorization = authToken;

  const token = getOneTimeToken();
  if (token) {
    output.headers["one-time-token"] = token;
  }
  if (pbacAuthorizationToken) {
    output.headers["pbac_authorization"] = pbacAuthorizationToken;
  }

  return output;
};

let buildAuthenticatedJsonForPUTRequest = (params, authToken) => {
  let output = buildJSONForPUTRequest(params);

  output.headers.Authorization = authToken;

  const token = getOneTimeToken();
  if (token) {
    output.headers["one-time-token"] = token;
  }

  return output;
};

let buildAuthenticatedJsonForGETRequest = (authToken) => {
  let output = buildJSONForGETRequest();

  output.headers.Authorization = authToken;

  const token = getOneTimeToken();
  if (token) {
    output.headers["one-time-token"] = token;
  }

  return output;
};

let buildAuthenticatedJsonForFileGETRequest = (authToken) => {
  let output = buildJSONForFileGETRequest();

  output.headers.Authorization = authToken;

  return output;
};

let buildAuthenticatedJsonForDELETERequest = (params = {}, authToken) => {
  let output = buildJSONForDELETERequest(params);

  output.headers.Authorization = authToken;

  return output;
};

/**
 * @class BaseProxy
 * @description Base module used for loading data from Sleek API.
 * @author Allan A. Chua
 * @version 1.0
 * @since October 03, 2018
 */
export default class BaseProxy {
  getClientType = () => {
    return isSleekDomain ? "main" : "manage_service";
  };

  /**
   * @async
   * @function getJsonDataViaPost
   * @description Method used for retrieving JSON data via post method.
   *
   * @param {String} url - The path of the endpoint found in sleek backend.
   * @param {Object} data - Parameters in object format.
   */
  async getJsonDataViaPost(url, data) {
    let response = await fetch(url, buildJSONForPOSTRequest(data));

    return await response.json();
  }

  /**
   * @async
   * @function getBackendUrl
   * @description Method used for retrieving the backend URL
   */
  async getSleekSignBackendUrl() {
    let appCustomEnv = await getAppCustomEnv();
    return `${appCustomEnv.VUE_APP_SLEEK_SIGN_BACKEND_URL}`;
  }

  /**
   * @async
   * @function getSleekBankBackendUrl
   * @description Method used for retrieving the backend URL
   */
  async getSleekBankBackendUrl() {
    let appCustomEnv = await getAppCustomEnv();
    if (!appCustomEnv.VUE_APP_SLEEK_BANK_BACKEND_URL) {
      throw new Error("SBA URL not found.");
    }
    return appCustomEnv.VUE_APP_SLEEK_BANK_BACKEND_URL;
  }

  /**
   * @async
   * @function getBackendUrl
   * @description Method used for retrieving the backend URL
   */
  async getBackendUrl() {
    let appCustomEnv = await getAppCustomEnv();
    return `${appCustomEnv.VUE_APP_PLATFORM_API}`;
  }

  /**
   * @async
   * @function getBackendUrl
   * @description Method used for retrieving the backend URL
   */
  async getAcquisitionUrl() {
    let appCustomEnv = await getAppCustomEnv();
    return `${appCustomEnv.VUE_APP_PLATFORM_ACQUISITION_API}`;
  }

  /**
   * @async
   * @function getSubscriptionBackendUrl
   * @description Method used for retrieving the backend URL
   */
  async getSubscriptionBackendUrl() {
    let appCustomEnv = await getAppCustomEnv();
    return `${appCustomEnv.VUE_APP_PLATFORM_SUBSCRIPTION_API}`;
  }

  /**
   * @async
   * @function getPaymentBackendUrl
   * @description Method used for retrieving the backend URL
   */
  async getPaymentBackendUrl() {
    let appCustomEnv = await getAppCustomEnv();
    return `${appCustomEnv.VUE_APP_PLATFORM_PAYMENT_API}`;
  }

  /**
   * @async
   * @function getCodingEngineUrl
   * @description Method used for retrieving the backend URL
   */
  async getCodingEngineUrl() {
    let appCustomEnv = await getAppCustomEnv();
    return `${appCustomEnv.VUE_APP_CODING_ENGINE_BACKEND_URL}`;
  }

  /**
   * @async
   * @function getBankStatementUrl
   * @description Method used for retrieving the backend URL
   */
  async getBankStatementUrl() {
    let appCustomEnv = await getAppCustomEnv();
    return `${appCustomEnv.VUE_APP_BANK_STATEMENT_BACKEND_URL}`;
  }

  /**
   * @async
   * @function getFileUrl
   * @description Method used for retrieving the backend URL
   */
  async getFileUrl() {
    const appCustomEnv = await getAppCustomEnv();
    // return String(appCustomEnv.VUE_APP_PLATFORM_API);
    const platformConfig = vueStore.state.configModule.platformConfig;
    if (appCustomEnv.VUE_APP_FILE_API_BYPASS_TOGGLE) {
      filesLogger.info("Bypassing Files MS toggle, redirecting request to Files MS");
      return String(appCustomEnv.VUE_APP_FILE_API);
    }

    filesLogger.info("Platform Config Empty", isEmpty(platformConfig));
    const filesServiceConfig = getAppFeatureProp(platformConfig.cmsGeneralFeatureList, "files_microservice");
    filesLogger.info("Service Config Available", !isEmpty(filesServiceConfig));
    filesLogger.info("Config", filesServiceConfig);
    if (filesServiceConfig && filesServiceConfig.enabled) {
      filesLogger.info("Enabled");
      if (appCustomEnv.VUE_APP_FILE_API) {
        filesLogger.info("Base API URL available on environment variables");
        const localDevelopment = getAppFeatureProp(filesServiceConfig.props, "local_development");
        const localDevelopmentEnabled = localDevelopment && localDevelopment.enabled;
        const env = appCustomEnv.VUE_APP_SLEEK_ENV;
        if ((localDevelopmentEnabled && env === "development") || env !== "development") {
          return String(appCustomEnv.VUE_APP_FILE_API);
        } else {
          filesLogger.info("Development mode disabled");
        }
      } else {
        filesLogger.warn("Base API URL not available on /env.json, redirecting request to main API");
      }
    }
    filesLogger.warn("Disabled or config not available, redirecting request to main API");
    return String(appCustomEnv.VUE_APP_PLATFORM_API);
  }

  /**
   * @async
   * @function getMyInfoUrl
   * @description Method used for retrieving the my info URL
   */
  async getMyInfoUrl() {
    let appCustomEnv = await getAppCustomEnv();
    return `${appCustomEnv.VUE_APP_MY_INFO_URL}`;
  }

  /**
   * @async
   * @function getSleekAccessControlUrl
   * @description Method used for retrieving the sleek access control URL
   */
  async getSleekAccessControlUrl() {
    let appCustomEnv = await getAppCustomEnv();
    return `${appCustomEnv.VUE_APP_SLEEK_ACCESS_CONTROL_URL}`;
  }

  /**
   * @async
   * @function getSleekSiteOnboardingUrl
   * @description Method used for retrieving the backend URL
   */
  async getSleekSiteOnboardingUrl() {
    let appCustomEnv = await getAppCustomEnv();
    return `${appCustomEnv.VUE_APP_SLEEK_SITE_ONBOARDING_BACKEND_URL}`;
  }

  /**
   * @async
   * @function getSleekSurveyUrl
   * @description Method used for retrieving the sleek survey control URL
   */
  async getSleekSurveyUrl() {
    let appCustomEnv = await getAppCustomEnv();
    return `${appCustomEnv.VUE_APP_SLEEK_SURVEY_URL}`;
  }

  async getSleekDeadlineUrl() {
    let appCustomEnv = await getAppCustomEnv();
    return `${appCustomEnv.VUE_APP_SLEEK_DEADLINE_URL}`;
  }

  /**
   * @async
   * @function postData
   * @description Method used for posting data to a target endpoint.
   * @param {String} url - The target endpoint URL.
   * @param {Object} data - Object parameters to be forwarded to the target endpoint.
   */
  async postData(url, data) {
    let response = await fetch(url, buildJSONForPOSTRequest(data));
    const requestId = getRequestId(response.headers);
    AuthEnforcer.checkResponseIfAuthorized(response.status);

    if (response.status === HTTP_STATUS_CODES.HTTP_NOT_FOUND_CODE) {
      throw Error([{ error_code: response.status, message: "Page not found" }]);
    }

    if (response.status >= HTTP_STATUS_CODES.HTTP_BASE_ERROR) {
      let errorResponse = await response.json();
      let error = new Error();
      if (errorResponse.message) {
        error = new Error(errorResponse.message);
      }

      if (errorResponse.length > 0) {
        if (errorResponse[0].message) {
          error = new Error(errorResponse[0].message);
        } else {
          error = new Error(errorResponse[0]);
        }
      }

      error.requestId = requestId;
      throw error;
    }

    let contentType = response.headers.get("content-type");
    if (contentType && contentType.indexOf("application/json") !== -1) {
      return await response.json();
    }

    return {};
  }

  /**
   * @async
   * @function postAuthenticatedData
   * @description Method used for posting data to a target endpoint with authorization header.
   * @param {*} url - The URL of the target endpoint.
   * @param {*} token - Authorization token of current user.
   * @param {*} data - Object parameters to be forwarded.
   */
  async postAuthenticatedData(url, token, data = {}, pbacAuthorizationToken) {
    let requestOptions = buildAuthenticatedJsonForPOSTRequest(data, token, pbacAuthorizationToken);
    let response = await fetch(url, requestOptions);
    AuthEnforcer.checkResponseIfAuthorized(response.status);

    if (response.status === HTTP_STATUS_CODES.HTTP_NOT_FOUND_CODE) {
      throw Error([{ error_code: "page_not_found", message: "Page not found" }]);
    }

    //@ Throws the error if it's greater than or equal to 300
    if (response.status >= HTTP_STATUS_CODES.HTTP_BASE_ERROR) {
      let errorResponse = await response.json();

      if (errorResponse.message) {
        throw Error(errorResponse.message);
      }

      if (errorResponse.length > 0) {
        if (errorResponse[0].message) {
          throw Error(errorResponse[0].message);
        } else {
          throw Error(errorResponse[0]);
        }
      }

      throw Error();
    }

    let contentType = response.headers.get("content-type");
    if (contentType && contentType.indexOf("application/json") !== -1) {
      return await response.json();
    } else if (contentType && contentType.indexOf("application/zip") !== -1) {
      return response;
    }

    return {};
  }

  /**
   * @async
   * @function postAuthenticatedFormData
   * @description Method used for posting formdata to a target endpoint with authorization header.
   * @param {*} url - The URL of the target endpoint.
   * @param {*} token - Authorization token of current user.
   * @param {*} data - Object parameters to be forwarded.
   */
  async postAuthenticatedFormData(url, token, formData, pbacAuthorizationToken) {
    let requestOptions = {
      method: "POST",
      headers: getFormDataJSONHeader(token, pbacAuthorizationToken),
      body: formData,
    };
    let response = await fetch(url, requestOptions);
    if (response.status === HTTP_STATUS_CODES.HTTP_NOT_FOUND_CODE) {
      throw Error([{ error_code: "page_not_found", message: "Page not found" }]);
    }
    if (response.status >= HTTP_STATUS_CODES.HTTP_BASE_ERROR) {
      let errorResponse = await response.json();

      if (errorResponse.message) {
        throw Error(errorResponse.message);
      }
      if (errorResponse.length > 0) {
        throw Error(errorResponse[0]);
      }
      throw Error();
    }

    let contentType = response.headers.get("content-type");
    if (contentType && contentType.indexOf("application/json") !== -1) {
      return await response.json();
    }
    return {};
  }

  async putAuthenticatedFormData(url, token, formData) {
    let requestOptions = {
      method: "PUT",
      headers: getFormDataJSONHeader(token),
      body: formData,
    };
    let response = await fetch(url, requestOptions);
    AuthEnforcer.checkResponseIfAuthorized(response.status);

    if (response.status === HTTP_STATUS_CODES.HTTP_NOT_FOUND_CODE) {
      throw Error([{ error_code: "page_not_found", message: "Page not found" }]);
    }
    if (response.status >= HTTP_STATUS_CODES.HTTP_BASE_ERROR) {
      let errorResponse = await response.json();

      if (errorResponse.message) {
        throw Error(errorResponse.message);
      }
      if (errorResponse.length > 0) {
        if (errorResponse[0].message) {
          throw Error(errorResponse[0].message);
        } else {
          throw Error(errorResponse[0]);
        }
      }
      throw Error();
    }

    let contentType = response.headers.get("content-type");
    if (contentType && contentType.indexOf("application/json") !== -1) {
      return await response.json();
    }
    return {};
  }

  /**
   * @async
   * @function putAuthenticatedData
   * @description Method used for PUT request to a target endpoint with authorization header.
   * @param {*} url - The URL of the target endpoint.
   * @param {*} token - Authorization token of current user.
   * @param {*} data - Object parameters to be forwarded.
   */
  async putAuthenticatedData(url, token, data) {
    let requestOptions = buildAuthenticatedJsonForPUTRequest(data, token);
    let response = await fetch(url, requestOptions);
    AuthEnforcer.checkResponseIfAuthorized(response.status);

    if (response.status === HTTP_STATUS_CODES.HTTP_NOT_FOUND_CODE) {
      throw Error([{ error_code: "page_not_found", message: "Page not found" }]);
    }

    if (response.status >= HTTP_STATUS_CODES.HTTP_BASE_ERROR) {
      let errorResponse = await response.json();
      if (errorResponse.message) {
        throw Error(errorResponse.message);
      }
      if (errorResponse.length > 0) {
        throw Error(errorResponse[0]);
      }
      throw Error();
    }

    let contentType = response.headers.get("content-type");
    if (contentType && contentType.indexOf("application/json") !== -1) {
      return await response.json();
    }
    return {};
  }

  /**
   * @async
   * @function getUnauthenticatedData
   * @description Method used for GET requests to a target endpoint.
   * @param {String} url - The URL of the target endpoint.
   * @param {String} options.authToken - Authorization token of current user.
   */
  async getUnauthenticatedData(url, { authToken }) {
    let requestOptions = buildJSONForGETRequest();
    // requestOptions.headers["Pragma"] = "no-cache";
    // requestOptions.headers["Cache-Control"] = "no-cache";
    if (authToken) {
      requestOptions.headers.Authorization = authToken;
    }

    let response = await fetch(url, requestOptions);
    return await response.json();
  }

  /**
   * @async
   * @function getAuthenticatedData
   * @description Method used for GET requests to a target endpoint with authorization header.
   * @param {*} url - The URL of the target endpoint.
   * @param {*} token - Authorization token of current user.
   * @param {*} data - Object parameters to be forwarded.
   */
  async getAuthenticatedData(url, token) {
    let requestOptions = buildAuthenticatedJsonForGETRequest(token);
    let response = await fetch(url, requestOptions);
    AuthEnforcer.checkResponseIfAuthorized(response.status);
    return await response.json();
  }

  /**
   * @async
   * @function getAuthenticatedUncachedData
   * @description Method used for GET requests to a target endpoint with authorization and no-cache header.
   * @param {*} url - The URL of the target endpoint.
   * @param {*} token - Authorization token of current user.
   * @param {*} data - Object parameters to be forwarded.
   */
  async getAuthenticatedUncachedData(url, token) {
    let requestOptions = buildAuthenticatedJsonForGETRequest(token);
    requestOptions.headers["Cache-Control"] = "no-cache";
    let response = await fetch(url, requestOptions);
    AuthEnforcer.checkResponseIfAuthorized(response.status);
    return await response.json();
  }

  async getDataFromSleekAllowedEndpoints(url) {
    // being used by AU Beta Onboarding to call 'https://company-checker.sleek.com/company' for our public company name checker
    let requestOptions = {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    };
    let response = await fetch(url, requestOptions);
    AuthEnforcer.checkResponseIfAuthorized(response.status);
    return response.json();
  }

  /**
   * @async
   * @function getAuthenticatedFileData
   * @description Method used for GET requests for files.
   * @param {*} url - The URL of the target endpoint.
   * @param {*} token - Authorization token of current user.
   */
  async getAuthenticatedFileData(url, token, headers = {}) {
    let requestOptions = buildAuthenticatedJsonForFileGETRequest(token);
    if (headers) {
      requestOptions.headers = {
        ...requestOptions.headers,
        ...headers,
      };
    }
    let response = await fetch(url, requestOptions);
    return response;
  }

  /**
   * @async
   * @function postAuthenticatedFileData
   * @description Method used for posting data to a target endpoint with authorization header.
   * @param {*} url - The URL of the target endpoint.
   * @param {*} token - Authorization token of current user.
   * @param {*} data - Object parameters to be forwarded.
   */
  async postAuthenticatedFileData(url, token, data = {}, headers = {}) {
    let requestOptions = buildAuthenticatedJsonForPOSTRequest(data, token);
    if (headers) {
      requestOptions.headers = {
        ...requestOptions.headers,
        ...headers,
      };
    }
    let response = await fetch(url, requestOptions);
    AuthEnforcer.checkResponseIfAuthorized(response.status);

    if (response.status === HTTP_STATUS_CODES.HTTP_NOT_FOUND_CODE) {
      throw Error([{ error_code: "page_not_found", message: "Page not found" }]);
    }

    //@ Throws the error if it's greater than or equal to 300
    if (response.status >= HTTP_STATUS_CODES.HTTP_BASE_ERROR) {
      let errorResponse = await response.json();

      if (errorResponse.message) {
        throw Error(errorResponse.message);
      }

      if (errorResponse.length > 0) {
        if (errorResponse[0].message) {
          throw Error(errorResponse[0].message);
        } else {
          throw Error(errorResponse[0]);
        }
      }

      throw Error();
    }

    let contentType = response.headers.get("content-type");
    if (contentType && contentType.indexOf("application/json") !== -1) {
      return await response.json();
    } else if (contentType && contentType.indexOf("application/zip") !== -1) {
      return response;
    }

    return {};
  }

  /**
   * @async
   * @function deleteAuthenticatedData
   * @description Method used for GET requests to a target endpoint with authorization header.
   * @param {*} url - The URL of the target endpoint.
   * @param {*} token - Authorization token of current user.
   * @param {*} data - Object parameters to be forwarded.
   */
  async deleteAuthenticatedData(url, token, data = {}) {
    let requestOptions = buildAuthenticatedJsonForDELETERequest(data, token);
    let response = await fetch(url, requestOptions);

    if (response.status === HTTP_STATUS_CODES.HTTP_NOT_FOUND_CODE) {
      throw Error([{ error_code: "page_not_found", message: "Page not found" }]);
    }

    if (response.status >= HTTP_STATUS_CODES.HTTP_BASE_ERROR) {
      let errorResponse = await response.json();
      if (errorResponse.message) {
        throw Error(errorResponse.message);
      }
      if (errorResponse.length > 0) {
        throw Error(errorResponse[0]);
      }
      throw Error();
    }

    let contentType = response.headers.get("content-type");
    if (contentType && contentType.indexOf("application/json") !== -1) {
      return await response.json();
    }
    return {};
  }

  getAppOrigin() {
    return getAppOrigin();
  }

  async getSleekAccessControlData(url) {
    let requestOptions = buildJSONForGETRequest();
    let response = await fetch(url, requestOptions);
    return await response.json();
  }

  /**
   * @async
   * @function getSupplierRuleServiceUrl
   * @description Method used for retrieving the backend URL of supplier rule service
   */
  async getSupplierRuleServiceUrl() {
    let appCustomEnv = await getAppCustomEnv();
    return `${appCustomEnv.VUE_APP_SUPPLIER_RULE_SERVICE_API}`;
  }
}

fetchIntercept.register({
  response: function (response) {
    // Modify the reponse object
    if (response.status == 429) {
      alert("You have too many requests, Please try again later.");
    }
    return response;
  },
});
